import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'openblock-vm';
import {injectIntl, intlShape} from 'react-intl';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeAccountSettingsModal,
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeLoginModal,
    closeMyProjectsModal,
    closeTelemetryModal,
    openAccountSettingsModal,
    closeAdminPanelModal,
    openAdminPanelModal,
    closeSaveProjectModal,
    openExtensionLibrary,
    openLoginModal,
    openMyProjectsModal
} from '../reducers/modals';
import {linkCloudProject, renameCloudProject, unlinkCloudProject} from '../reducers/cloud-project';
import {fetchCurrentUser} from '../lib/auth-api.js';
import {
    getIsCheckingSession,
    getIsLoggedIn,
    loginSuccess,
    sessionCheckEnd,
    sessionCheckStart
} from '../reducers/auth';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import storage from '../lib/storage';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {setIsScratchDesktop} from '../lib/isScratchDesktop.js';
import {
    install as installScratchApi,
    loadProject as loadProjectFromUrl,
    uninstall as uninstallScratchApi
} from '../lib/scratch-api.js';
import {
    install as installHostBridge,
    readEnvironment,
    uninstall as uninstallHostBridge
} from '../lib/host-bridge.js';
import {isAccountUiEnabled} from '../lib/scratch-config.js';
import {setFullScreen, setPlayer} from '../reducers/mode';
import {setProjectTitle} from '../reducers/project-title';

// === 示例代码库 ===
import ExampleLibrary from './example-library.jsx';

class GUI extends React.Component {
    constructor (props) {
        super(props);
        // Whether a host platform embedded this page is answered by the URL, so
        // it is known before the first render and the account entry never
        // flickers in and out.
        this.state = {hostEmbedded: Boolean(readEnvironment(window.location.search))};
    }
    componentDidMount () {
        setIsScratchDesktop(this.props.isScratchDesktop);
        this.props.onStorageInit(storage);
        this.props.onVmInit(this.props.vm);
        // The host page owns the accounts, so there is no session of our own to
        // restore unless it explicitly asked for the built-in account screens.
        if (isAccountUiEnabled()) this.props.onRestoreSession();
        installScratchApi({
            vm: this.props.vm,
            getProjectTitle: () => this.props.projectTitle,
            setProjectTitle: this.props.onSetProjectTitle,
            setPlayer: this.props.onSetPlayer,
            setFullScreen: this.props.onSetFullScreen
        });
        // An embedded editor is a different window from its host, so the host
        // cannot reach window.scratch and asks over postMessage instead. The API
        // above has to be installed first, because the host's project is opened
        // through it.
        installHostBridge({
            vm: this.props.vm,
            loadProject: loadProjectFromUrl,
            getProjectTitle: () => this.props.projectTitle,
            setProjectTitle: this.props.onSetProjectTitle
        });
    }

    componentDidUpdate (prevProps) {
        if (this.props.projectId !== prevProps.projectId && this.props.projectId !== null) {
            this.props.onUpdateProjectId(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
    }

    componentWillUnmount () {
        uninstallHostBridge();
        uninstallScratchApi();
    }
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            exampleLibraryVisible,
            isError,
            isScratchDesktop,
            isShowingProject,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <>
                <GUIComponent
                    hostEmbedded={this.state.hostEmbedded}
                    loading={fetchingProject || isLoading || loadingStateVisible}
                    {...componentProps}
                >
                    {children}
                </GUIComponent>
                {/* 示例代码库弹窗 */}
                {exampleLibraryVisible && (
                    <ExampleLibrary vm={this.props.vm} />
                )}
            </>
        );
    }
}

GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onProjectLoaded: PropTypes.func,
    onRestoreSession: PropTypes.func,
    onSetFullScreen: PropTypes.func,
    onSetPlayer: PropTypes.func,
    onSetProjectTitle: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    telemetryModalVisible: PropTypes.bool,
    exampleLibraryVisible: PropTypes.bool,
    currentUser: PropTypes.shape({
        id: PropTypes.string,
        account: PropTypes.string,
        displayName: PropTypes.string,
        avatarUrl: PropTypes.string
    }),
    isLoggedIn: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isCheckingSession: PropTypes.bool,
    loginModalVisible: PropTypes.bool,
    adminPanelVisible: PropTypes.bool,
    saveProjectModalVisible: PropTypes.bool,
    myProjectsModalVisible: PropTypes.bool,
    accountSettingsModalVisible: PropTypes.bool,
    projectTitle: PropTypes.string,
    onOpenAccountSettingsModal: PropTypes.func,
    onOpenLoginModal: PropTypes.func,
    onOpenAdminPanelModal: PropTypes.func,
    onOpenMyProjectsModal: PropTypes.func,
    onProfileUpdated: PropTypes.func,
    onRequestCloseAccountSettingsModal: PropTypes.func,
    onRequestCloseAdminPanel: PropTypes.func,
    onRequestCloseSaveProjectModal: PropTypes.func,
    onRequestCloseMyProjectsModal: PropTypes.func,
    onRequestCloseLoginModal: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired,
    isRealtimeMode: PropTypes.bool
};

GUI.defaultProps = {
    isScratchDesktop: false,
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        uploadProgressVisible: state.scratchGui.modals.uploadProgress,
        compileProgressVisible: state.scratchGui.modals.compileProgress,
        updateModalVisible: state.scratchGui.modals.updateModal,
        exampleLibraryVisible: state.scratchGui.modals.exampleLibrary,
        currentUser: state.scratchGui.auth.user,
        // Derived from the role the service reports, never from anything the
        // browser could set on its own.
        isAdmin: Boolean(state.scratchGui.auth.user &&
            state.scratchGui.auth.user.role === 'admin'),
        linkedProjectId: state.scratchGui.cloudProject.projectId,
        isLoggedIn: getIsLoggedIn(state.scratchGui.auth),
        isCheckingSession: getIsCheckingSession(state.scratchGui.auth),
        loginModalVisible: state.scratchGui.modals.loginModal,
        adminPanelVisible: state.scratchGui.modals.adminPanelModal,
        saveProjectModalVisible: state.scratchGui.modals.saveProjectModal,
        myProjectsModalVisible: state.scratchGui.modals.myProjectsModal,
        accountSettingsModalVisible: state.scratchGui.modals.accountSettingsModal,
        projectTitle: state.scratchGui.projectTitle,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm,
        isRealtimeMode: state.scratchGui.programMode.isRealtimeMode
    };
};

const mapDispatchToProps = dispatch => ({
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    onSetPlayer: isPlayerOnly => dispatch(setPlayer(isPlayerOnly)),
    onSetFullScreen: isFullScreen => dispatch(setFullScreen(isFullScreen)),
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseLoginModal: () => dispatch(closeLoginModal()),
    onRequestCloseAdminPanel: () => dispatch(closeAdminPanelModal()),
    onRequestCloseSaveProjectModal: () => dispatch(closeSaveProjectModal()),
    onRequestCloseMyProjectsModal: () => dispatch(closeMyProjectsModal()),
    onRequestCloseAccountSettingsModal: () => dispatch(closeAccountSettingsModal()),
    onOpenAdminPanelModal: () => dispatch(openAdminPanelModal()),
    onOpenMyProjectsModal: () => dispatch(openMyProjectsModal()),
    onOpenAccountSettingsModal: () => dispatch(openAccountSettingsModal()),
    onProfileUpdated: user => dispatch(loginSuccess(user)),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal()),
    onOpenLoginModal: () => dispatch(openLoginModal()),
    onRestoreSession: () => {
        dispatch(sessionCheckStart());
        return fetchCurrentUser()
            .then(user => dispatch(loginSuccess(user)))
            .catch(() => dispatch(sessionCheckEnd()));
    },
    // Opening or creating a project inside "我的作品" also makes it the target
    // of the menu bar save button.
    onProjectOpened: (projectId, name) => dispatch(linkCloudProject(projectId, name)),
    onProjectRenamed: (projectId, name) => dispatch(renameCloudProject(projectId, name)),
    onProjectUnlinked: () => dispatch(unlinkCloudProject()),
    // Signing out must not leave one account's project attached to the next
    // session.
    onSessionEnded: () => dispatch(unlinkCloudProject())
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps,
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
